import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';
import { ArrowRight, Sparkles, Megaphone, BookOpen, Users, FileText, Crown, Camera } from 'lucide-react';

export function ForOrganizers() {
  const features = [
    {
      icon: Crown,
      title: '1:1 Consultation',
      desc: 'Event website audit, registration page audit, paid ads audit, and strategy session. $297 one-time.',
      href: '/organizers/consultation',
      cta: 'Book Now →'
    },
    {
      icon: Megaphone,
      title: 'Event Promotion',
      desc: 'Featured placement in VentyVIP listings. Get your event discovered by the right audience.',
      href: '/organizers',
      cta: 'List Your Event →'
    },
    {
      icon: Sparkles,
      title: 'AI Event Guides',
      desc: 'Auto-generated attendee briefs with logistics, tips, FAQs, and what to expect.',
      href: '/#guide',
      cta: 'Generate Guide →'
    },
    {
      icon: BookOpen,
      title: 'Promotion Playbook',
      desc: 'Best practices for event listings, imagery, copy, and maximizing attendance.',
      href: '/organizers/playbook',
      cta: 'Read Guide →'
    },
    {
      icon: Users,
      title: 'Venue Partnerships',
      desc: 'Co-marketing opportunities with rooftop bars, hotels, and premier venues.',
      href: '/organizers',
      cta: 'Explore Partners →'
    },
    {
      icon: FileText,
      title: 'Organizer Resources',
      desc: 'Downloadable checklists, planning templates, and vendor directories.',
      href: '/organizers',
      cta: 'Download →'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Nav */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        padding: '16px 24px',
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--line)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={28} />
          </a>
          <a 
            href="/" 
            style={{ 
              fontSize: 14, 
              color: 'var(--ink-2)', 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            ← Back to VentyVIP
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '140px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span style={{ 
            display: 'inline-block', 
            padding: '6px 14px', 
            background: 'var(--primary-tint)', 
            color: 'var(--primary)', 
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 24
          }}>
            For Event Organizers
          </span>
          
          <h1 style={{ 
            fontSize: 'clamp(36px, 6vw, 56px)', 
            fontWeight: 600, 
            lineHeight: 1.1, 
            marginBottom: 20,
            color: 'var(--ink)'
          }}>
            Amplify Your Event.<br />
            <span style={{ color: 'var(--primary)' }}>Effortlessly.</span>
          </h1>
          
          <p style={{ 
            fontSize: 'clamp(17px, 2.5vw, 20px)', 
            color: 'var(--ink-2)', 
            maxWidth: 560, 
            margin: '0 auto 40px',
            lineHeight: 1.6
          }}>
            Tools to promote, plan, and pack your events with the right audience. 
            From AI-generated guides to featured listings.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="/#guide"
              style={{
                padding: '14px 28px',
                background: 'var(--primary)',
                color: '#fff',
                borderRadius: 999,
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
              }}
            >
              <Sparkles size={18} />
              Create AI Guide
            </a>
            <a 
              href="/organizers"
              style={{
                padding: '14px 28px',
                background: 'var(--bg)',
                color: 'var(--ink)',
                borderRadius: 999,
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid var(--line)'
              }}
            >
              <Megaphone size={18} />
              List Your Event
            </a>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section style={{ padding: '60px 24px 100px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 28, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 16,
            color: 'var(--ink)'
          }}>
            Everything you need to succeed
          </h2>

          {/* Consultation CTA */}
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <a 
                href="/organizers/consultation"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: '#1a1a1a',
                  borderRadius: 999,
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(255, 215, 0, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 215, 0, 0.3)';
                }}
              >
                👑 Book 1:1 Consultation — $297
              </a>
              <a 
                href="/organizers/photography"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                  color: '#ffffff',
                  borderRadius: 999,
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(139, 92, 246, 0.3)';
                }}
              >
                <Camera size={18} /> Book Event Photography
              </a>
            </div>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', maxWidth: 500, margin: '0 auto' }}>
              Event website audit • Registration page audit • Paid Ads audit • Event strategy session
            </p>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
            gap: 24 
          }}>
            {features.map((feature, i) => {
              const isConsultation = feature.title === '1:1 Consultation';
              return (
                <div 
                  key={i}
                  style={{
                    padding: 32,
                    background: isConsultation ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : 'var(--bg-2)',
                    borderRadius: 16,
                    border: isConsultation ? '1px solid #E6B800' : '1px solid var(--line)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = isConsultation ? '0 12px 32px rgba(255, 215, 0, 0.25)' : '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: isConsultation ? 'rgba(26,26,26,0.15)' : 'var(--primary-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20
                  }}>
                    <feature.icon size={24} style={{ color: isConsultation ? '#1a1a1a' : 'var(--primary)' }} />
                  </div>
                  
                  <h3 style={{ 
                    fontSize: 20, 
                    fontWeight: 600, 
                    marginBottom: 12,
                    color: isConsultation ? '#1a1a1a' : 'var(--ink)'
                  }}>
                    {feature.title}
                  </h3>
                  
                  <p style={{ 
                    fontSize: 15, 
                    color: isConsultation ? '#333' : 'var(--ink-2)', 
                    lineHeight: 1.6,
                    marginBottom: 20
                  }}>
                    {feature.desc}
                  </p>
                
                <a 
                  href={feature.href}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: isConsultation ? '#1a1a1a' : 'var(--primary)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  {feature.cta}
                  <ArrowRight size={14} />
                </a>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Success */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-2)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 32, fontWeight: 600, marginBottom: 16, color: 'var(--ink)' }}>
            Featured on VentyVIP
          </h2>
          <p style={{ fontSize: 17, color: 'var(--ink-2)', marginBottom: 40 }}>
            Bring your event to the front row, with a paid Featured Event. Promote on VentyVip and equip your attendees with the right information!
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: 32, 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            opacity: 0.6
          }}>
            {['Freedom 250 GP', 'Ai4 Conference', 'AFWF', 'Grand Prix 250'].map((name, i) => (
              <span key={i} style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                color: 'var(--ink-3)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '-0.02em'
              }}>
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 24px' }}>
        <div style={{ 
          maxWidth: 600, 
          margin: '0 auto', 
          textAlign: 'center',
          padding: 48,
          background: 'var(--primary-tint)',
          borderRadius: 24,
          border: '1px solid rgba(59,130,246,0.2)'
        }}>
          <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 12, color: 'var(--ink)' }}>
            Ready to grow your audience?
          </h2>
          <p style={{ fontSize: 16, color: 'var(--ink-2)', marginBottom: 28 }}>
            Start with a free AI event guide, or submit your event for featured placement.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="/#guide"
              style={{
                padding: '14px 28px',
                background: 'var(--primary)',
                color: '#fff',
                borderRadius: 999,
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500
              }}
            >
              Create Guide
            </a>
            <a 
              href="/organizers"
              style={{
                padding: '14px 28px',
                background: 'transparent',
                color: 'var(--ink)',
                borderRadius: 999,
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500,
                border: '1px solid var(--line)'
              }}
            >
              List Event
            </a>
          </div>
        </div>

        {/* Banner Ad */}
        <HighPerfAd />
      </section>

      {/* Footer */}
      <footer style={{ padding: '40px 24px', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <Wordmark size={20} />
          <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
            <Link to="/about" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>About</Link>
            <Link to="/privacy" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Terms</Link>
            <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Contact</a>
          </div>
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>© 2026 VentyVIP</span>
        </div>
      </footer>
    </div>
  );
}

// High Performance Ad Component
const HighPerfAd = () => {
  const adRef = useRef(null);

  useEffect(() => {
    if (!adRef.current) return;

    // Check if script already exists
    if (document.querySelector('script[data-highperf-ad]')) return;

    // Set atOptions
    window.atOptions = {
      'key': '1b0b64e165f51047dc499dd798f66e18',
      'format': 'iframe',
      'height': 90,
      'width': 728,
      'params': {}
    };

    const script = document.createElement('script');
    script.src = 'https://www.highperformanceformat.com/1b0b64e165f51047dc499dd798f66e18/invoke.js';
    script.async = true;
    script.setAttribute('data-highperf-ad', 'true');

    adRef.current.appendChild(script);
  }, []);

  return (
    <div style={{ margin: '48px auto 0', maxWidth: '728px', textAlign: 'center', background: 'var(--bg-2)', borderRadius: '8px', padding: '16px' }}>
      <div ref={adRef} style={{ maxWidth: '728px', margin: '0 auto', minHeight: '90px' }} />
      <p style={{ textAlign: 'center', fontSize: '11px', color: 'var(--ink-4)', margin: '8px 0 0' }}>Advertisement.</p>
    </div>
  );
};
